import { useQuery } from '@apollo/client';
import { OndemandVideo } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { string } from 'yup';
import { useDeleteChannel, useUpdateChannel } from '~/api/channels';
import { applyServerErrors } from '~/api/helpers';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { ScrollableRouterTabs } from '~/components/RouterTabs';
import { Tooltip } from '~/components/Tooltip';
import { DeleteButton, SaveButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import { Timestamps } from '~/components/timestamps';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { ChannelEncryptionKind } from '~/generated/graphql';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { ChannelForm, channelSchema } from '../components/form';
import { NetworkChannelDetailsDocument } from '../queries/details.generated';
import { useTabs } from './lib/tabs';

// An existing channel already has a thumbnail; only a replacement is sent.
const channelUpdateSchema = channelSchema.shape({
  thumbnailUri: string().label('Thumbnail'),
});

export const ChannelSettings = () => {
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();

  const navigate = useNavigate();

  const link = useLink();

  const params = useParams<{ channelId: string }>();

  const channelId = Number(params.channelId);

  const tabs = useTabs();

  const { data, loading, error } = useQuery(NetworkChannelDetailsDocument, {
    variables: { networkId: currentNetwork.id, channelId },
  });

  const [deleteChannel] = useDeleteChannel();
  const [updateChannel] = useUpdateChannel();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const channel = data?.network?.channel;

  const initialValues = useMemo(
    () => ({
      backgroundColor: channel?.backgroundColor ?? '',
      enabledForLiveStreaming: channel?.enabledForLiveStreaming ?? false,
      encryption: channel?.encryption ?? ChannelEncryptionKind.None,
      jpegxsEnabled: channel?.jpegxsEnabled ?? false,
      name: channel?.name ?? '',
      number: channel?.number ?? '',
      source: channel?.source ?? '',
      thumbnailUri: '',
      url: channel?.url ?? '',
      urls: channel?.urls ?? [],
      outputUrl: channel?.outputUrl ?? '',
      outputUrls: channel?.outputUrls ?? [],
    }),
    [channel],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values, { setErrors }) => {
      const patch = { ...values, thumbnailUri: values.thumbnailUri.trim() || undefined };
      const result = await updateChannel({ variables: { input: { id: channelId, patch } } });
      if (applyServerErrors(result, setErrors) || result.errors) return;
      formik.resetForm({ values });
    },
    validateOnMount: true,
    validationSchema: channelUpdateSchema,
  });

  const handleDelete = useCallback(async () => {
    if (!(await confirmDelete())) return;
    await deleteChannel({
      variables: { input: { channelIds: [channelId] } },
    });
    navigate(link('/channels'), { replace: true });
  }, [confirmDelete, deleteChannel, channelId, link, navigate]);

  if ((!loading && !data) || error) return <Navigate replace to="/not-found" />;

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      <>
        <Toolbar
          actions={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <SaveButton
                disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
                type="submit"
                form="update-channel"
              />
              <Tooltip title={data?.network?.channel.canDestroy.reasons?.fullMessages.join(', ')}>
                <span>
                  <DeleteButton
                    disabled={!data?.network?.channel.canDestroy.value}
                    onClick={handleDelete}
                  />
                </span>
              </Tooltip>
            </Box>
          }
          breadcrumbsLabel={<RouterBreadcrumbs />}
          titleIcon={<OndemandVideo />}
          titleText={data?.network?.channel.name}
        />
        <ScrollableRouterTabs current={t('common.labels.details')} tabs={tabs} />
        <PageContainer>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ChannelForm
              formik={formik}
              formId="update-channel"
              thumbnailFileUri={data?.network?.channel.thumbnailFile.uri}
            />
            {currentUser.admin && (
              <Timestamps
                createdAt={data?.network?.channel.createdAt ?? ''}
                updatedAt={data?.network?.channel.updatedAt ?? ''}
              />
            )}
          </Box>
        </PageContainer>
        <ConfirmDialog
          {...confirmDeleteProps}
          confirm={t('common.dialogs.permanently_delete')}
          deleteConfirm
          prompt={
            <span>
              {t('pages.channels.dialogs.delete.detail_prompt')}{' '}
              <b>{t('common.warnings.cannot_be_undone')}</b>
            </span>
          }
          title={t('pages.channels.dialogs.delete.title')}
        />
      </>
    </LoadingPane>
  );
};
